import React, { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { HeroIcons } from '../../../lib/utils/heroIcons'
import EmblaCarousel from '../../EmblaCarousel'
import PropertyService from '../../../services/property.service'
import useLocale from '../../../hooks/useLocale'
import {
  MaskedProperty,
  Property,
  PropertyWithAccess,
} from '../../../types/Property/Property'
import UpcomingPropertySlide from './UpcomingPropertySlide'
import SeeAllOpportunitiesSlide from './SeeAllOpportunitiesSlide'
import LatestFundingPropertySlide from './LatestFundingPropertySlide'
import LatestFundedPropertySlide from './LatestFundedPropertySlide'
import routes from '../../../config/routes'
import Link from 'next/link'
import { GoogleTagManagerService } from '../../../services/google-tag-manager.service'
import { mapItem } from '../../../lib/utils/helperGtm'

export default function LatestListingCarousel() {
  const { t } = useTranslation('home')
  const { locale } = useLocale()

  const [upcomingProperties, setUpcomingProperties] = useState<
    PropertyWithAccess[]
  >([])
  const [latestFundedProperties, setLatestFundedProperties] = useState<
    PropertyWithAccess[]
  >([])
  const [latestFundingProperties, setLatestFundingProperties] = useState<
    PropertyWithAccess[]
  >([])
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const fetchCarouselProperties = async () => {
    const shouldShowLoading =
      latestFundingProperties.length === 0 &&
      upcomingProperties.length === 0 &&
      latestFundedProperties.length === 0

    if (shouldShowLoading) {
      setIsLoading(true)
    }

    try {
      const resProperties = await PropertyService.getProperties({
        page: 1,
        pageSize: 10,
        filterBy: [
          'fundingStatus',
          'fundingStatus',
          'fundingStatus',
          'fundingStatus',
        ],
        filterOperator: ['=', '=', '=', '='],
        filter: ['funding', 'not_started', 'funded', 'reimbursed'],
        filterCondition: 'or',
        orderBy: 'createdAt',
        order: 'DESC',
        lang: locale,
      })

      const funding: PropertyWithAccess[] = []
      const upcoming: PropertyWithAccess[] = []
      const funded: PropertyWithAccess[] = []

      for (const property of resProperties.properties as PropertyWithAccess[]) {
        if (property.id === 'b7c91c4a-52a2-4e0a-864f-1d4c95ba85b7') continue
        if (property.fundingStatus === 'funding' && funding.length < 2) {
          funding.push(property)
        }
        if (property.fundingStatus === 'not_started' && upcoming.length < 2) {
          upcoming.push(property)
        }
        if (
          ['funded', 'reimbursed'].includes(property.fundingStatus) &&
          funded.length < 2
        ) {
          funded.push(property)
        }

        if (
          funding.length === 2 &&
          upcoming.length === 2 &&
          funded.length === 2
        ) {
          break
        }
      }

      setLatestFundingProperties(funding)
      setUpcomingProperties(upcoming)
      setLatestFundedProperties(funded)
    } catch (e) {}
    setIsLoading(false)
  }

  const handlePropertyClick = (
    property: Property | MaskedProperty,
    itemListId: string,
    itemListName: string,
  ): void => {
    GoogleTagManagerService.ClickItem({
      itemListId,
      itemListName,
      item: mapItem(property),
    })
  }

  useEffect(() => {
    fetchCarouselProperties()
  }, [locale])

  const slides: React.JSX.Element[] = isLoading
    ? Array.from({ length: 3 }).map((_, index) => (
        <div
          key={'loadingSlide' + index}
          className="h-full w-full animate-pulse bg-neutralGray/20"
        />
      ))
    : [
        ...latestFundingProperties.map((property, index) => (
          <div key={'latestFundingSlide' + index} className="relative h-full">
            <LatestFundingPropertySlide
              property={property}
              onClick={(property) =>
                handlePropertyClick(
                  property,
                  'home_latest_funding',
                  'Derniers financements en cours',
                )
              }
            />
          </div>
        )),
        ...upcomingProperties.map((property, index) => (
          <div key={'upcomingSlide' + index} className="relative h-full">
            <UpcomingPropertySlide
              property={property}
              onClick={(property) =>
                handlePropertyClick(
                  property,
                  'home_upcoming_property',
                  'Prochain financements',
                )
              }
            />
          </div>
        )),
        ...latestFundedProperties.map((property, index) => (
          <div key={'latestFundedSlide' + index} className="relative h-full">
            <LatestFundedPropertySlide
              property={property}
              onClick={(property) =>
                handlePropertyClick(
                  property,
                  'home_latest_funded',
                  'Derniers financements',
                )
              }
            />
          </div>
        )),
        <SeeAllOpportunitiesSlide key={'seeAllOpportunitiesSlide'} />,
      ]

  return (
    <>
      <Link href={routes.properties} className="flex w-fit items-center pb-1.5">
        <p className="font-bold text-neutralBlack/80">{t('carousel.title')}</p>
        <HeroIcons.ChevronRightIcon className="h-5 w-5 text-neutralBlack/80" />
      </Link>
      <div>
        <EmblaCarousel
          slides={slides}
          options={{ align: 'start', containScroll: 'trimSnaps' }}
        />
      </div>
    </>
  )
}
